// Shared view-selection core for editable bulk tables (price and label
// update). These tables are OmegaForms addressed positionally, so the rows
// array must never move: search, filters and sort are a projection — a list
// of ORIGINAL indices in display order — computed here, never a reorder.
import * as S from "effect-app/Schema"

export type SortDir = "asc" | "desc"

export interface ViewSort<K extends string> {
  key: K
  dir: SortDir
}

export const EmptyFilter = S.Literals(["all", "onlyEmpty", "hideEmpty"])
export type EmptyFilter = typeof EmptyFilter.Type

export const emptyFilterOptions: { title: string; value: EmptyFilter }[] = [
  { title: "Alle", value: "all" },
  { title: "Nur leere", value: "onlyEmpty" },
  { title: "Leere ausblenden", value: "hideEmpty" }
]

export const cycleSort = <K extends string>(current: ViewSort<K> | null, key: K): ViewSort<K> | null => {
  if (current === null || current.key !== key) return { key, dir: "asc" }
  return current.dir === "asc" ? { key, dir: "desc" } : null
}

export const keepByEmptyFilter = (filter: EmptyFilter, isEmpty: boolean): boolean =>
  filter === "all" || (filter === "onlyEmpty" ? isEmpty : !isEmpty)

export type SortValue = string | number | null

// The caller's SortKey fixes the value type per key, so the mixed case is
// unreachable; numbers must never travel through localeCompare, where 9
// would sort after 10.
export const compareValues = (a: SortValue, b: SortValue): number => {
  if (typeof a === "number" && typeof b === "number") return a - b
  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
  }
  return 0
}

/*
 * Filter first, then sort — reporting ORIGINAL indices throughout. The sort
 * is stable (ties keep build order) and puts null values last in both
 * directions.
 */
export function selectIndices<T, K extends string>(
  rows: readonly T[],
  sort: ViewSort<K> | null,
  visible: (row: T) => boolean,
  sortValueOf: (row: T, key: K) => SortValue
): number[] {
  const indices: number[] = []
  rows.forEach((row, index) => {
    if (visible(row)) indices.push(index)
  })

  if (sort === null) return indices

  const dir = sort.dir === "asc" ? 1 : -1
  return indices
    .map((index) => ({ index, value: sortValueOf(rows[index]!, sort.key) }))
    .sort((a, b) => {
      if (a.value === null && b.value === null) return a.index - b.index
      if (a.value === null) return 1
      if (b.value === null) return -1
      const compared = compareValues(a.value, b.value)
      return compared === 0 ? a.index - b.index : dir * compared
    })
    .map((entry) => entry.index)
}
